import { useState, useEffect } from "react";
import { formValues } from "@/components/appointmentDashboard/appointmentTreeView";
import { useTranslation } from "react-i18next";

import DefaultForm from "@/components/forms/defaultForm";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import classNames from "classnames";
import styles from "@/components/forms/findingForm.module.css";
interface Props {
  findingId?: string;
  formId?: string;
  formValues?: formValues | formValues[];
  formValuesLoaded?: boolean;
  onFormTitleChange?: (formTitle: string) => void;
  onSuccessfulFormSubmitHandler?: () => void;
  onAdditionalUploadSuccess?: () => void;
  onCancelFormHandler: () => void;
  preSubmitConfirmMessage?: string;
  readOnly?: boolean;
  withPaper?: boolean;
  onCloseModal?: () => void;
  statusOfAppointment?: string;
  fieldsScrollWithinForm?: boolean;
  pageLayout?: boolean;
  guardUnsavedChanges?: boolean;
}
export default function FindingForm(props: Props) {
  const { t } = useTranslation();
  const [formValues, setFormValues]: any = useState<Record<string, unknown>>(
    {}
  );
  const [formTitle, setFormTitle] = useState<string>("");
  const [formId, setFormId] = useState<string | undefined>(props.formId);
  const [formfieldsDefinition, setFormfieldsDefinition] = useState<string>("");
  const [formfieldsVisibility, setFormfieldsVisibility]: any = useState({});
  const [formfieldsVisibilityRulesets, setFormfieldsVisibilityRulesets]: any =
    useState({});
  const [
    allowAdditionalMeasurementUploads,
    setAllowAdditionalMeasurementUploads,
  ] = useState<boolean>(false);
  const [formImageSet, setFormImageSet] = useState<any[]>([]);
  const [formNoteItem, setFormNoteItem] = useState<any | null>(null);
  const [initialFormValuesApplied, setInitialFormValuesApplied] =
    useState<boolean>(false);

  const { handleError, errorModal } = useErrorModal();
  const { showConfirmationModalAsync } = useConfirmationModal();

  const findingId = props.findingId;

  useEffect(() => {
    if (findingId !== "") {
      const additionalUrlParameters: any = {};
      additionalUrlParameters["latest_version"] = "true";
      api.genericApiRequest({
        entity: "form",
        method: "get",
        parametersToRender: {
          additionalUrlParameters: additionalUrlParameters,
        },
        successHandler: (res: any) => {
          const forms = res.data;

          setFormTitle(forms.name);
          setFormfieldsDefinition(forms.measurementdefinitiontoform_set);
          setFormId(forms.id);
          setAllowAdditionalMeasurementUploads(
            forms?.enable_additional_measurement_uploads
          );
          // }
        },
        failHandler: (error: any) => {
          handleError(error);
        },
        entityId: formId,
      });
    }
  }, []);

  useEffect(() => {
    if (props.formValuesLoaded === false || !Array.isArray(props.formValues)) {
      return;
    }
    const nextFormValues: any = {};
    for (const formValue of props.formValues) {
      if (!formValue.name) continue;
      nextFormValues[formValue.name] = formValue.value;
    }
    setFormValues(nextFormValues);
    setInitialFormValuesApplied(true);
  }, [props.formValues, props.formValuesLoaded]);

  useEffect(() => {
    if (
      !Array.isArray(formfieldsDefinition) ||
      formfieldsDefinition.length === 0
    ) {
      return;
    }

    // Inital note & imageSets building so that they are present
    const initialImageSet = formfieldsDefinition.reduce((acc, field) => {
      const name = field?.measurement_definition?.name;
      if (name) {
        acc[name] = {
          image_set: [],
          measurement_id: null,
          measurement_name: name,
        };
      }
      return acc;
    }, {} as Record<string, { image_set: any[]; measurement_id: string | null; measurement_name: string }>);

    const initialNoteItem = formfieldsDefinition.reduce((acc, field) => {
      const name = field?.measurement_definition?.name;
      if (name) {
        acc[name] = {
          note: {},
          measurement_id: null,
          measurement_name: name,
        };
      }
      return acc;
    }, {} as Record<string, { note: any; measurement_id: string | null; measurement_name: string }>);

    // if props.formValues has any values - merge for the measurement and include the rest of the initial sets
    if (Array.isArray(props.formValues)) {
      props.formValues.forEach((item) => {
        if (!item.name) return;
        if (initialImageSet[item.name]) {
          initialImageSet[item.name] = {
            ...initialImageSet[item.name],
            image_set: item.image_set ?? [],
            measurement_id: item.id,
          };
        }
        if (initialNoteItem[item.name]) {
          initialNoteItem[item.name] = {
            ...initialNoteItem[item.name],
            note: item.note ?? {},
            measurement_id: item.id,
          };
        }
      });
    }

    setFormImageSet(initialImageSet);
    setFormNoteItem(initialNoteItem);
  }, [props.formValues, formfieldsDefinition]);

  useEffect(() => {
    recalculateFormfieldVisiblity(formfieldsVisibilityRulesets);
  }, [formValues]);

  useEffect(() => {
    if (props.onFormTitleChange) {
      props.onFormTitleChange(formTitle);
    }
  }, [formTitle]);

  useEffect(() => {
    const rulesets = prepareVisibilityRulesets(formfieldsDefinition);
    setFormfieldsVisibilityRulesets(rulesets);
    recalculateFormfieldVisiblity(rulesets);
  }, [formfieldsDefinition]);

  const prepareVisibilityRulesets = function (fields: any) {
    const rulesets: any = {};
    for (const field in fields) {
      const item = fields[field];
      const fieldname = item.measurement_definition.name;

      if (item.visibility_ruleset) {
        let visibility_ruleset = item.visibility_ruleset;
        if (
          typeof item.visibility_ruleset === "string" ||
          item.visibility_ruleset instanceof String
        ) {
          try {
            visibility_ruleset = JSON.parse(item.visibility_ruleset);
          } catch {
            // invalid ruleset JSON must not break the whole form - the field stays visible
            continue;
          }
        }

        if (
          visibility_ruleset &&
          typeof visibility_ruleset === "object" &&
          Object.entries(visibility_ruleset).length > 0
        ) {
          rulesets[fieldname] = visibility_ruleset;
        }
      }
    }

    return rulesets;
  };

  const recalculateFormfieldVisiblity = function (rulesets: any) {
    if (rulesets && Object.keys(rulesets).length > 0) {
      const newFormfieldsVisibility: any = {};
      for (const key of Object.keys(rulesets)) {
        const currentFieldRules = rulesets[key];
        newFormfieldsVisibility[key] =
          parseAndApplyVisibilityRules(currentFieldRules);
      }

      setFormfieldsVisibility(newFormfieldsVisibility);
    }
  };

  const parseAndApplyVisibilityRules = function (visibility_ruleset: any) {
    const visibleResults = [];

    for (const visibility_ruleset_item of Object.entries(visibility_ruleset)) {
      const visibility_ruleset_item_name = visibility_ruleset_item[0];
      const visibility_ruleset_item_value: any = visibility_ruleset_item[1];
      if (
        typeof visibility_ruleset_item_value === "object" &&
        Object.entries(visibility_ruleset_item_value).length > 0
      ) {
        for (const single_rule of Object.entries(
          visibility_ruleset_item_value
        )) {
          visibleResults.push(
            validateVisibilityRule(visibility_ruleset_item_name, single_rule)
          );
        }
      }
    }

    if (visibleResults.includes(false)) {
      return false;
    }

    return true;
  };

  const getValueMapForField = function (fieldName: string) {
    if (!Array.isArray(formfieldsDefinition)) {
      return [];
    }
    const field: any = formfieldsDefinition.find(
      (item: any) => item?.measurement_definition?.name === fieldName
    );
    const valueMap = field?.measurement_definition?.value_map;
    return Array.isArray(valueMap) ? valueMap : [];
  };

  const normalizeForComparison = function (value: any) {
    return String(value).trim().toLowerCase();
  };

  const unwrapOptionValue = function (item: any) {
    return item && typeof item === "object" && "value" in item
      ? item.value
      : item;
  };

  // a selection matches a rule either by its stored value or by its value_map label
  const buildComparisonCandidates = function (rawItem: any, valueMap: any[]) {
    const value = unwrapOptionValue(rawItem);
    if (value === undefined || value === null || value === "") {
      return [];
    }
    const normalizedValue = normalizeForComparison(value);
    const candidates = [normalizedValue];

    if (rawItem && typeof rawItem === "object" && rawItem.label != null) {
      candidates.push(normalizeForComparison(rawItem.label));
    }
    for (const entry of valueMap) {
      const [label, mappedValue]: any = Object.entries(entry)[0];
      if (normalizeForComparison(mappedValue) === normalizedValue) {
        candidates.push(normalizeForComparison(label));
      }
    }

    return candidates;
  };

  const validateVisibilityRule = function (valueName: string, rule: any) {
    const rule_type = rule[0];
    const rule_value = rule[1];
    const valueMap = getValueMapForField(valueName);
    const rawValue = formValues[valueName];

    const rawItems = Array.isArray(rawValue) ? rawValue : [rawValue];
    const candidates: string[] = [];
    for (const rawItem of rawItems) {
      candidates.push(...buildComparisonCandidates(rawItem, valueMap));
    }

    const matchesRuleValue = (ruleValue: any) =>
      (typeof ruleValue === "string" ||
        typeof ruleValue === "number" ||
        typeof ruleValue === "boolean") &&
      candidates.includes(normalizeForComparison(ruleValue));

    switch (rule_type) {
      case "is":
        return matchesRuleValue(rule_value);
      case "not":
        return !matchesRuleValue(rule_value);
      case "lt":
      case "gt": {
        const scalarValue = Array.isArray(rawValue)
          ? undefined
          : unwrapOptionValue(rawValue);
        if (
          scalarValue === undefined ||
          scalarValue === null ||
          scalarValue === ""
        ) {
          return false;
        }
        const left = Number(scalarValue);
        const right = Number(rule_value);
        if (!Number.isNaN(left) && !Number.isNaN(right)) {
          return rule_type === "lt" ? left < right : left > right;
        }
        return rule_type === "lt"
          ? scalarValue < rule_value
          : scalarValue > rule_value;
      }
      case "in":
        return Array.isArray(rule_value) && rule_value.some(matchesRuleValue);
      case "nin":
        return Array.isArray(rule_value) && !rule_value.some(matchesRuleValue);
      case "contains": {
        if (!Array.isArray(rawValue)) {
          return false;
        }
        const containsRuleValues = Array.isArray(rule_value)
          ? rule_value
          : [rule_value];
        return containsRuleValues.some(matchesRuleValue);
      }
    }

    return false;
  };

  const handleFormItemChange = function (event: any) {
    if (event.target.name) {
      setFormValues({ ...formValues, [event.target.name]: event.target.value });
    }
  };

  const handleDropDownChange = function (selectedOption: any, label: any) {
    let fieldName = label;
    let rawValue = selectedOption;

    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.target
    ) {
      if (fieldName === undefined && selectedOption.target.name) {
        fieldName = selectedOption.target.name;
      }
      rawValue = selectedOption.target.value;
    }

    let selectedValues: any;
    if (Array.isArray(rawValue)) {
      selectedValues = rawValue.map(unwrapOptionValue);
    } else if (rawValue === null || rawValue === undefined) {
      // select was cleared - reset so dependent fields hide again
      selectedValues = "";
    } else {
      selectedValues = unwrapOptionValue(rawValue);
    }

    if (!fieldName) {
      return;
    }

    handleFormItemChange({
      target: { id: fieldName, value: selectedValues, name: fieldName },
    });
  };

  const renderFormFields = function (fields: any) {
    const inputElements: any = [];
    for (const field in fields) {
      const item = fields[field];
      const fieldname = item.measurement_definition.name;
      const label = item.measurement_definition.label;
      const type = item.measurement_definition.type;

      let options: any = [];
      if (type === "choice" || type === "multiselect") {
        if (
          Array.isArray(item.measurement_definition.value_map) &&
          item.measurement_definition.value_map.length !== 0
        ) {
          options = item.measurement_definition.value_map.map((obj: any) => {
            const [label, value] = Object.entries(obj)[0];
            return { value: value, label: label };
          });
        }
      }

      inputElements.push({
        // preDefinedValues: formValues[fieldname],
        type: type,
        name: fieldname,
        label: label,
        possibleRemedy: item.measurement_definition.possible_remedy,
        isGbu: item.measurement_definition.is_gbu,
        options: options,
        visibility: formfieldsVisibility,
        required: item.mandatory,
        errorMessageRequired: "Bitte mache eine Eingabe",
        errorMessageValidation:
          "Unzulässige Zeichen. Bitte passe deine Eingabe an.",
        // fieldname: fieldname,
        onChange: handleFormItemChange,
        onDropdownChange: handleDropDownChange,
        readOnly: props.readOnly,
        order: item.order,
      });
    }
    return (
      <DefaultForm
        withPaper={props.withPaper}
        allowAdditionalMeasurementUploads={allowAdditionalMeasurementUploads}
        onAdditionalUploadSuccess={props.onAdditionalUploadSuccess}
        entity={"finding"}
        entityId={formId}
        targetEntity={"measurement/submit_measurements"}
        statusOfAppointment={props.statusOfAppointment}
        preDefinedData={formValues}
        fieldsScrollWithinForm={props.fieldsScrollWithinForm}
        pageLayout={props.pageLayout}
        guardUnsavedChanges={props.guardUnsavedChanges}
        formImageSet={formImageSet}
        formNoteItem={formNoteItem}
        input={inputElements}
        visibility={formfieldsVisibility}
        findingId={findingId}
        onSuccessHandler={props.onSuccessfulFormSubmitHandler}
        cancelButton={true}
        preSubmitConfirmMessage={props.preSubmitConfirmMessage}
        onCancelFormHandler={async () => {
          const confirmed = await showConfirmationModalAsync(
            t("confirm_cancel_form_input")
          );

          if (confirmed) {
            props.onCancelFormHandler();
          }
        }}
        onCloseModal={props.onCloseModal}
      />
    );
  };

  const waitingForFormValues =
    props.formValuesLoaded === false ||
    (Array.isArray(props.formValues) && !initialFormValuesApplied);

  let formFields: any = <></>;

  if (formfieldsDefinition != "" && !waitingForFormValues) {
    formFields = renderFormFields(formfieldsDefinition);
  }

  return (
    <>
      <div
        className={classNames({
          [styles.fillAvailableHeight]: props.fieldsScrollWithinForm && !props.pageLayout,
        })}
      >
        {formFields}
      </div>
      {errorModal}
    </>
  );
}
